/**
 * pages/AuthNotification.tsx
 *
 * Public page accessed when a user clicks the "Voir" CTA in a notification email.
 * Reads the single-use token from the query string, calls verify-notification-token
 * to validate it and obtain a fresh Supabase magic link, then immediately redirects
 * the browser to that link which auto-authenticates and lands the user on /clients.
 *
 * If the token is expired or already used, shows a clear message with a
 * "Se connecter" button that redirects to /login?redirect=/clients.
 *
 * Created: 2026-03-30
 * Updated 2026-04-14:
 *   - Redesigned loading and error states: logo image, flex-col centering, refined typography.
 *   - Redirect destination changed to /clients (handled by edge function redirect_path).
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Spinner from '../components/ui/Spinner';
import Button from '../components/ui/Button';

type State = 'loading' | 'error' | 'expired';

export default function AuthNotification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setState('expired');
      setErrorMessage('Aucun token trouvé dans le lien.');
      return;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

    fetch(`${supabaseUrl}/functions/v1/verify-notification-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type') ?? '';
        let data: Record<string, unknown> = {};
        if (contentType.includes('application/json')) {
          try {
            data = await res.json();
          } catch {
            const fallback = await res.text().catch(() => '');
            setState('error');
            setErrorMessage(
              `Réponse inattendue du serveur (HTTP ${res.status})${fallback ? ': ' + fallback.slice(0, 200) : ''}.`
            );
            return;
          }
        } else {
          const body = await res.text().catch(() => '');
          setState(res.status === 410 ? 'expired' : 'error');
          setErrorMessage(
            `Réponse inattendue du serveur (HTTP ${res.status})${body ? ': ' + body.slice(0, 200) : ''}.`
          );
          return;
        }
        if (res.status === 410) {
          setState('expired');
          setErrorMessage((data.error as string | undefined) ?? 'Ce lien a expiré ou a déjà été utilisé.');
          return;
        }
        if (!res.ok || !data.action_link) {
          setState('error');
          setErrorMessage((data.error as string | undefined) ?? 'Une erreur est survenue.');
          return;
        }
        window.location.href = data.action_link as string;
      })
      .catch(() => {
        setState('error');
        setErrorMessage('Impossible de contacter le serveur. Vérifiez votre connexion.');
      });
  }, [searchParams]);

  const handleLogin = () => {
    navigate('/login?redirect=%2Fclients');
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
        <div className="flex flex-col items-center gap-6">
          <img src="/logo-qollect.png" alt="Qollect" className="h-8 w-auto" />
          <Spinner size="lg" className="text-primary-600" />
          <p className="text-sm text-neutral-500 tracking-wide">Connexion en cours…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-soft-xl border border-neutral-100 p-8 flex flex-col items-center gap-6">
        <img src="/logo-qollect.png" alt="Qollect" className="h-7 w-auto" />
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-11 h-11 rounded-full bg-amber-50 flex items-center justify-center">
            <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h1 className="text-base font-semibold text-neutral-900">
            {state === 'expired' ? 'Lien expiré ou déjà utilisé' : 'Une erreur est survenue'}
          </h1>
          <p className="text-sm text-neutral-500 leading-relaxed">{errorMessage}</p>
          {state === 'expired' && (
            <p className="text-sm text-neutral-400">
              Connectez-vous normalement pour accéder à vos clients.
            </p>
          )}
        </div>
        <Button fullWidth onClick={handleLogin}>
          Se connecter
        </Button>
      </div>
    </div>
  );
}
